import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE from '../config';

function VerifyRegistration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null); // 'full' | 'partial' | null
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  useEffect(() => {
    fetchVerification();
  }, [token]);

  const fetchVerification = async () => {
    try {
      const response = await fetch(`${API_BASE}/registrations/verify/${token}`);
      if (!response.ok) {
        setError('Registrazione non trovata.');
        setLoading(false);
        return;
      }
      const result = await response.json();
      setData(result);
      // Pre-select all participants that are NOT already checked in
      const unchecked = result.participants.map((p, i) => (!p.checkedIn ? i : null)).filter(i => i !== null);
      setSelectedParticipants(unchecked);
      setLoading(false);
    } catch (err) {
      setError('Errore nel caricamento.');
      setLoading(false);
    }
  };

  const toggleParticipant = (index) => {
    // Can only toggle participants that are not already checked in
    if (data.participants[index].checkedIn) return;
    setSelectedParticipants(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleCheckin = async () => {
    if (selectedParticipants.length === 0) return;
    setCheckingIn(true);
    try {
      const response = await fetch(`${API_BASE}/registrations/verify/${token}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participantIndices: selectedParticipants })
      });
      if (response.ok) {
        const result = await response.json();
        // Determine if this was a full or partial check-in
        const totalParticipants = data.participants.length;
        const alreadyCheckedIn = data.participants.filter(p => p.checkedIn).length;
        const nowCheckedIn = alreadyCheckedIn + selectedParticipants.length;
        setCheckinResult(nowCheckedIn >= totalParticipants ? 'full' : 'partial');
        setData(prev => ({
          ...prev,
          checkedIn: result.checkedIn,
          checkedInPartial: result.checkedInPartial,
          checkedInAt: result.checkedInAt,
          participants: prev.participants.map((p, i) => ({
            ...p,
            checkedIn: p.checkedIn || selectedParticipants.includes(i)
          }))
        }));
      }
    } catch (err) {
      console.error('Check-in failed:', err);
    }
    setCheckingIn(false);
  };

  if (loading) return (
    <div className="verify-page">
      <p>Caricamento...</p>
    </div>
  );

  if (error) return (
    <div className="verify-page">
      <div className="verify-status verify-invalid">
        ❌ {error}
      </div>
    </div>
  );

  // Check-in confirmation screen
  if (checkinResult) {
    const checkedCount = data.participants.filter(p => p.checkedIn || selectedParticipants.includes(data.participants.indexOf(p))).length;
    return (
      <div className="verify-page">
        <div className="verify-status" style={{
          background: checkinResult === 'full' ? '#27ae60' : '#2980b9',
          color: '#fff',
          fontSize: '20px',
          padding: '30px',
          borderRadius: '8px'
        }}>
          {checkinResult === 'full' ? '✅ CHECK-IN COMPLETO' : '🔵 CHECK-IN PARZIALE'}
        </div>
        <div className="verify-section" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>
            <strong>{data.student.firstName} {data.student.lastName}</strong> — Classe {data.student.class}
          </p>
          <p style={{ fontSize: '16px', color: '#666' }}>
            {checkinResult === 'full'
              ? `${data.participants.length}/${data.participants.length} partecipanti entrati`
              : `${checkedCount}/${data.participants.length} partecipanti entrati`
            }
          </p>
          {checkinResult === 'partial' && (
            <p style={{ fontSize: '13px', color: '#888', marginTop: '10px' }}>
              I restanti partecipanti potranno entrare con una nuova scansione del QR code.
            </p>
          )}
        </div>
        {/* Back to dashboard button if opened from dashboard */}
        {window.history.length > 1 && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button className="btn-secondary" onClick={() => navigate(-1)}>← Torna al Dashboard</button>
          </div>
        )}
      </div>
    );
  }

  const isPaid = data.paid;
  const isFullyCheckedIn = data.checkedIn;
  const isPartiallyCheckedIn = data.checkedInPartial;
  const liberatoriaReceived = data.liberatoriaReceived;
  const liberatoriaSignedElectronically = data.liberatoriaSignedElectronically;
  const hasDelegation = data.delegation && data.delegation.active;
  const alreadyCheckedInCount = data.participants.filter(p => p.checkedIn).length;
  const hasUncheckedParticipants = alreadyCheckedInCount < data.participants.length;

  // Determine issues for orange banner
  const issues = [];
  if (!isPaid) issues.push('Pagamento non confermato');
  if (!liberatoriaReceived && !liberatoriaSignedElectronically) issues.push('Liberatoria non ricevuta');
  if (hasDelegation) {
    if (data.delegation.mode === 'electronic') {
      if (!data.delegation.delegatingParentSigned || !data.delegation.delegatedParentSigned) {
        issues.push('Delega elettronica — firma incompleta (' +
          (data.delegation.delegatingParentSigned ? '1' : '0') + '/2 firme)');
      }
      // If both signed, no issue
    } else {
      issues.push('Delega attiva — verificare documento di delega firmato');
    }
  }
  const allGreen = issues.length === 0;

  return (
    <div className="verify-page" style={{ paddingBottom: hasUncheckedParticipants ? '100px' : '20px' }}>
      {/* Fully checked in warning */}
      {isFullyCheckedIn && (
        <div className="verify-status verify-invalid">
          ⚠️ ATTENZIONE: TUTTI I PARTECIPANTI SONO GIÀ ENTRATI
          <div style={{ fontSize: '13px', fontWeight: 'normal', marginTop: '5px' }}>
            Primo ingresso: {data.checkedInAt ? data.checkedInAt.substring(0, 10).split('-').reverse().join('/') + ' alle ' + data.checkedInAt.substring(11, 16) : 'N/A'}
          </div>
        </div>
      )}

      {/* Partial check-in info banner */}
      {isPartiallyCheckedIn && !isFullyCheckedIn && (
        <div className="verify-status" style={{ background: '#2980b9', color: '#fff', borderRadius: '8px', padding: '15px' }}>
          🔵 CHECK-IN PARZIALE — {alreadyCheckedInCount}/{data.participants.length} partecipanti già entrati
          <div style={{ fontSize: '13px', fontWeight: 'normal', marginTop: '5px' }}>
            Primo ingresso: {data.checkedInAt ? data.checkedInAt.substring(0, 10).split('-').reverse().join('/') + ' alle ' + data.checkedInAt.substring(11, 16) : 'N/A'}
          </div>
        </div>
      )}

      {/* Main status banner (only if no check-in yet) */}
      {!isFullyCheckedIn && !isPartiallyCheckedIn && (
        <div className={`verify-status ${allGreen ? 'verify-paid' : 'verify-unpaid'}`}>
          {allGreen
            ? '✅ REGISTRAZIONE VALIDA — TUTTO IN ORDINE'
            : '⚠️ REGISTRAZIONE VALIDA — ATTENZIONE'}
          {!allGreen && (
            <div style={{ fontSize: '13px', fontWeight: 'normal', marginTop: '8px', textAlign: 'left' }}>
              {issues.map((issue, i) => (
                <div key={i} style={{ marginTop: '4px' }}>⚠️ {issue}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Student info */}
      <div className="verify-section">
        <h3>Studente</h3>
        <div className="verify-field">
          <span className="verify-label">Nome:</span>
          <span>{data.student.firstName} {data.student.lastName}</span>
        </div>
        <div className="verify-field">
          <span className="verify-label">Classe:</span>
          <span>{data.student.class}</span>
        </div>
      </div>

      {/* Participants with checkmarks */}
      <div className="verify-section">
        <h3>Partecipanti ({data.participants.length})</h3>
        {data.participants.map((p, i) => {
          const isAlreadyIn = p.checkedIn;
          const isSelected = selectedParticipants.includes(i);
          return (
            <div
              key={i}
              onClick={() => toggleParticipant(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                margin: '6px 0',
                borderRadius: '8px',
                border: isAlreadyIn ? '1px solid #bdc3c7' : (isSelected ? '2px solid #27ae60' : '2px solid #ddd'),
                background: isAlreadyIn ? '#f0f0f0' : (isSelected ? '#eafaf1' : '#fff'),
                cursor: isAlreadyIn ? 'default' : 'pointer',
                opacity: isAlreadyIn ? 0.7 : 1
              }}
            >
              {/* Checkmark */}
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: isAlreadyIn ? '2px solid #95a5a6' : (isSelected ? '2px solid #27ae60' : '2px solid #bdc3c7'),
                background: isAlreadyIn ? '#95a5a6' : (isSelected ? '#27ae60' : '#fff'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                flexShrink: 0
              }}>
                {(isAlreadyIn || isSelected) && (
                  <span style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>✓</span>
                )}
              </div>
              {/* Info */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: isAlreadyIn ? '#7f8c8d' : '#2c3e50' }}>
                  {p.firstName} {p.lastName}
                </div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {p.relation} — {p.menu}
                </div>
              </div>
              {/* Already in badge */}
              {isAlreadyIn && (
                <span style={{ fontSize: '11px', color: '#95a5a6', fontWeight: '500' }}>Già entrato</span>
              )}
            </div>
          );
        })}
        <div className="verify-total">
          {data.participants.length} partecipanti — €{data.totalCost}
        </div>
      </div>

      {/* Delegation */}
      {hasDelegation && (
        <div className="verify-section verify-delegation">
          <h3>🤝 Delega Attiva</h3>
          <div className="verify-field">
            <span className="verify-label">Genitore delegato:</span>
            <span>{data.delegation.delegatedParent.firstName} {data.delegation.delegatedParent.lastName}</span>
          </div>
          <div className="verify-field">
            <span className="verify-label">Genitore di:</span>
            <span>{data.delegation.delegatedStudent.firstName} {data.delegation.delegatedStudent.lastName} (Classe {data.delegation.delegatedStudent.class})</span>
          </div>
        </div>
      )}

      {/* Sticky bottom check-in bar */}
      {hasUncheckedParticipants && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '2px solid #27ae60',
          padding: '12px 16px',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            👆 Selezionare i partecipanti e confermare l'ingresso
          </div>
          <button
            onClick={handleCheckin}
            disabled={checkingIn || selectedParticipants.length === 0}
            style={{
              width: '100%',
              maxWidth: '500px',
              padding: '16px 24px',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#fff',
              background: selectedParticipants.length === 0 ? '#bdc3c7' : '#27ae60',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedParticipants.length === 0 ? 'default' : 'pointer',
              touchAction: 'manipulation'
            }}
          >
            {checkingIn ? 'Conferma in corso...' : `✅ CONFERMA INGRESSO (${selectedParticipants.length} partecipanti)`}
          </button>
        </div>
      )}

      {/* Back to dashboard button */}
      {!hasUncheckedParticipants && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button className="btn-secondary" onClick={() => navigate(-1)}>← Torna al Dashboard</button>
        </div>
      )}
    </div>
  );
}

export default VerifyRegistration;
